import config from 'config';
import { APIResponse } from 'types/Cokiba';

export interface Link {
  id: number;
  order: number;
  name: string;
  description: string;
  url: string;
  section: string;
}

export interface LinkInput {
  id: number;
  order: number;
  name: string;
  description: string;
  url: string;
  section: string;
  pdf?: File | null;
}

type GetLinkResponse = APIResponse<Link[]>;
type DeleteLinkResponse = APIResponse<undefined>;
type CreateLinkResponse = APIResponse<Link>;
type UpdateLinkResponse = APIResponse<Link>;
type UpdateOrderResponse = APIResponse<boolean>;

const ERROR_MESSAGE = 'Ocurrió un problema inesperado al momento de procesar los enlaces.';

function authToken(): string | null {
  return localStorage.getItem('accessToken');
}

function buildLinkBody(link: LinkInput): { body: BodyInit; headers: Record<string, string> } {
  const token = authToken();
  const headers: Record<string, string> = {
    Authorization: token || '',
  };

  if (link.pdf) {
    const fd = new FormData();
    fd.append('order', String(link.order));
    fd.append('name', link.name);
    fd.append('description', link.description || '');
    fd.append('url', link.url || '');
    fd.append('section', link.section);
    fd.append('pdf', link.pdf);
    return { body: fd, headers };
  }

  headers['Content-Type'] = 'application/json';
  return {
    body: JSON.stringify({
      order: link.order,
      name: link.name,
      description: link.description,
      url: link.url,
      section: link.section,
    }),
    headers,
  };
}

export async function getLinks(section: string = 'home'): Promise<Link[]> {
  try {
    const token = authToken();
    if (!token) return [];

    const url = new URL(`${ config.baseUrl }/contents/links`);
    url.searchParams.set('section', section);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    });

    if (response.status !== 200) return [];

    const content = await response.json() as GetLinkResponse;
    return content.payload || [];
  } catch (err) {
    throw new Error(ERROR_MESSAGE);
  }
}

export async function deleteLink(id: number): Promise<boolean> {
  try {
    const token = authToken();
    if (!token) return false;

    const url = new URL(`${ config.baseUrl }/contents/links/${ id }`);
    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    });

    if (response.status !== 200) return false;

    const content = await response.json() as DeleteLinkResponse;
    return content.status == 'ok';
  } catch (err) {
    throw new Error(ERROR_MESSAGE);
  }
}

export async function createLink(link: LinkInput): Promise<Link | false> {
  try {
    const token = authToken();
    if (!token) return false;

    const { body, headers } = buildLinkBody(link);

    const url = new URL(`${ config.baseUrl }/contents/links`);
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
      body,
    });

    if (response.status !== 200) return false;

    const content = await response.json() as CreateLinkResponse;
    return content.payload || false;
  } catch (err) {
    throw new Error(ERROR_MESSAGE);
  }
}

export async function updateLink(link: LinkInput): Promise<Link | false> {
  try {
    const token = authToken();
    if (!token) return false;

    const { body, headers } = buildLinkBody(link);

    const url = new URL(`${ config.baseUrl }/contents/links/${ link.id }`);
    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers,
      body,
    });

    if (response.status !== 200) return false;

    const content = await response.json() as UpdateLinkResponse;
    return content.payload || false;
  } catch (err) {
    throw new Error(ERROR_MESSAGE);
  }
}

export async function updateOrder(
  links: Record<number, number>,
  section: string = 'home',
): Promise<boolean> {
  try {
    const token = authToken();
    if (!token) return false;

    const url = new URL(`${ config.baseUrl }/contents/links/order`);
    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({ order: links, section }),
    });

    if (response.status !== 200) return false;

    const content = await response.json() as UpdateOrderResponse;
    return content.payload || false;
  } catch (err) {
    throw new Error(ERROR_MESSAGE);
  }
}
